import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useReferenceDataAvailable } from "@/hooks/useReferenceDataAvailable";
import { ROLE_LABELS } from "@/utils/permissions";
import ChangePasswordModal from "./ChangePasswordModal";

const baseNavLinks = [
  { to: "/", label: "대시보드" },
  { to: "/cases", label: "사건관리" },
  { to: "/samples", label: "시료관리" },
  { to: "/analyses", label: "분석" },
  { to: "/reports", label: "보고서" },
  { to: "/search", label: "검색" },
];

/** 폭 제한 없이 전폭으로 쓰는 경로. */
const FULL_WIDTH_ROUTES = [
  /^\/analyses\/\d+\/dashboard/,
  /^\/reference-db/,
  /^\/cases\/\d+\/fire-debris/,
];

export default function Layout({ children }: { children: ReactNode }) {
  const { user, permissions, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { available: referenceDbAvailable } = useReferenceDataAvailable();

  useEffect(() => {
    if (!userMenuOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [userMenuOpen]);

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // 참조물질 DB: 데이터 적재 시에만 네비에 노출 (빈 탭 시연 리스크 방지)
  const navLinks = referenceDbAvailable
    ? [
        ...baseNavLinks.slice(0, 5),
        { to: "/reference-db", label: "참조물질 DB" },
        baseNavLinks[5],
      ]
    : baseNavLinks;

  const allLinks = permissions?.canManageUsers
    ? [...navLinks, { to: "/admin", label: "관리" }]
    : navLinks;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 상단 네비 고정 — 세로로 긴 화면(분석 대시보드·목록)에서 스크롤해도
          메뉴 접근 유지. z-40: 드롭다운(z-50)·모바일 메뉴(z-50) 아래, 본문 위.
          분석 대시보드 사이드바는 top-20(80px)이라 헤더(h-16=64px)와 안 겹침. */}
      <nav className="bg-white shadow sticky top-0 z-40">
        {/* 헤더는 전폭 사용 — max-w 제한 시 큰 화면에서도 네비 공간이 1280px로
            묶여 윈도우 한글 폰트 환경에서 좌우 그룹이 겹치는 문제가 있었음 */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 gap-x-4">
            <div className="flex">
              {/* 모바일 햄버거 버튼 (xl 미만: 좁은 노트북 배율에서 데스크톱 네비가 꺾이는 것 방지) */}
              <button
                type="button"
                aria-label="주 메뉴 열기"
                aria-expanded={mobileMenuOpen}
                className="lg:hidden inline-flex items-center justify-center gap-1 px-2 py-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
                <span className="text-sm font-medium">메뉴</span>
              </button>

              <div className="flex-shrink-0 flex items-center">
                <Link
                  to="/"
                  className="flex items-center gap-2 text-xl font-bold text-gray-900"
                >
                  <img
                    src="/logo-icon.png"
                    alt="위해기체 수사 플랫폼 로고"
                    className="h-10 w-auto"
                  />
                  <span className="hidden sm:inline">위해기체 수사 플랫폼</span>
                </Link>
              </div>
              {/* 데스크톱 메뉴 (xl 이상에서만 노출 — 그 미만은 햄버거 메뉴 사용) */}
              {/* 데스크톱 메뉴 — lg(1024px) 이상. 종전 기준은 xl(1280px) 이었는데
                  노트북 배율 125% 환경(1440→1152, 1366→1093)이 통째로 걸려
                  **상단 메뉴가 사라진 것처럼 보였다**(운영자 지적 2026-08-22).
                  실측: 로고 236 + 메뉴 533 + 우측 139 + 여백 ≈ 972px 이면 한 줄에
                  들어간다 → lg 로 낮추고 1024~1280 구간은 간격을 좁힌다. */}
              <div className="hidden lg:ml-4 lg:flex lg:space-x-0.5 xl:ml-6 xl:space-x-1 2xl:space-x-3">
                {allLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`inline-flex items-center px-2 xl:px-2.5 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap shrink-0 ${
                      isActive(link.to)
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    onClick={() => setUserMenuOpen((open) => !open)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap shrink-0"
                  >
                    <span className="font-medium">{user.username}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap shrink-0">
                      {ROLE_LABELS[user.role] ?? user.role}
                    </span>
                    <svg
                      aria-hidden="true"
                      className={`h-4 w-4 text-gray-400 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-52 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
                    >
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user.username}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {ROLE_LABELS[user.role] ?? user.role}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setPwModalOpen(true);
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        비밀번호 변경
                      </button>
                      <div className="border-t border-gray-100" />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        로그아웃
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={logout}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap shrink-0"
                >
                  로그아웃
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 모바일 슬라이드 메뉴 (xl 미만) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="text-lg font-bold text-gray-900">메뉴</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {user && (
              <div className="p-4 border-b bg-gray-50">
                <p className="text-sm font-medium text-gray-900">
                  {user.username}
                </p>
                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {ROLE_LABELS[user.role] ?? user.role}
                </span>
              </div>
            )}
            <div className="py-2">
              {allLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "bg-gray-100 text-gray-900 border-l-4 border-blue-500"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}

      <main
        className={
          // 전폭 화면: 스펙트럼·차트가 주인공이라 가로가 곧 정보량이다.
          // 분석 대시보드(사용자 시각 검증 채택) + 참조물질 DB(원본 NIST MS
          // Search 도 화면 전체를 쓴다 — 좌 목록 / 우 스펙트럼 분할).
          FULL_WIDTH_ROUTES.some((re) => re.test(location.pathname))
            ? "mx-auto px-4 sm:px-6 lg:px-8 py-8"
            : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        }
      >
        {children}
      </main>

      <ChangePasswordModal
        isOpen={pwModalOpen}
        onClose={() => setPwModalOpen(false)}
      />
    </div>
  );
}
